import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import PhoneInput, { formatPhoneNumber, parsePhoneNumber } from '@/components/PhoneInput';
import AddressInput, { formatAddress } from '@/components/AddressInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, Plus, Search, Edit, Trash2, Hotel, Users, 
  MoreVertical, Eye, Settings, CheckCircle, XCircle, Clock,
  AlertTriangle, Database, FileText, ShoppingCart, Package, Calculator, Store,
  ToggleLeft, ToggleRight, Target, Globe, MessageSquare, CreditCard, Layers
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import DeleteEntityDialog from '@/components/DeleteEntityDialog';
import { Switch } from '@/components/ui/switch';
import RoomTypeConfigDialog from '@/components/RoomTypeConfigDialog';
import RoomTypeAmenitiesTab from '@/components/RoomTypeAmenitiesTab';
import HotelAmenitiesAllocationDialog from '@/components/HotelAmenitiesAllocationDialog';

// Available modules configuration
const AVAILABLE_MODULES = [
  { id: 'purchase', name: 'Purchase', icon: ShoppingCart, description: 'Vendors, PRs, POs, GRN, Invoices', color: 'text-blue-600 bg-blue-100' },
  { id: 'finance', name: 'Finance', icon: Calculator, description: 'Finance Dashboard, Ledgers, Reports', color: 'text-green-600 bg-green-100' },
  { id: 'assets', name: 'Assets', icon: Package, description: 'Asset Management, Categories, Maintenance', color: 'text-purple-600 bg-purple-100' },
  { id: 'inventory', name: 'Inventory', icon: Store, description: 'Stores, Items, Stock Movements', color: 'text-orange-600 bg-orange-100' },
  { id: 'budget', name: 'Budget', icon: Target, description: 'Budget Planning, Expenditure Tracking', color: 'text-teal-600 bg-teal-100' },
  { id: 'portal', name: 'Online Portal', icon: Globe, description: 'Portal Settings, Property Listing, Users', color: 'text-indigo-600 bg-indigo-100' },
  { id: 'finance_setup', name: 'Finance Setup', icon: Settings, description: 'Account Groups, Ledgers, GST Rates', color: 'text-emerald-600 bg-emerald-100' },
  { id: 'communication', name: 'Communication', icon: MessageSquare, description: 'SMTP, Email Settings', color: 'text-pink-600 bg-pink-100' },
  { id: 'payment_gateway', name: 'Payment Gateway', icon: CreditCard, description: 'Razorpay, Payment Settings', color: 'text-cyan-600 bg-cyan-100' },
];

export default function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [showSecureDeleteDialog, setShowSecureDeleteDialog] = useState(false);  // New dual-OTP delete
  const [showModulesDialog, setShowModulesDialog] = useState(false);  // Module settings dialog
  const [showAddHotelDialog, setShowAddHotelDialog] = useState(false);  // Add Hotel dialog
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [dataSummary, setDataSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingModules, setSavingModules] = useState(false);
  const [moduleSettings, setModuleSettings] = useState([]);
  const [addingHotel, setAddingHotel] = useState(false);
  
  // Room Type Config dialog state
  const [roomTypeConfigDialogOpen, setRoomTypeConfigDialogOpen] = useState(false);
  const [selectedHotelForConfig, setSelectedHotelForConfig] = useState(null);
  
  // Amenities Allocation dialog state
  const [amenitiesDialogOpen, setAmenitiesDialogOpen] = useState(false);
  const [selectedHotelForAmenities, setSelectedHotelForAmenities] = useState(null);
  
  // Hotel form state
  const [hotelFormData, setHotelFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    admin_name: '',
    admin_email: '',
    admin_password: ''
  });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    legal_name: '',
    email: '',
    phone: '',
    address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pin_code: '',
      country: 'India'
    },
    pan_number: '',
    gstin: '',
    finance_coa_type: 'company'
  });

  useEffect(() => {
    fetchCompanies();
  }, [statusFilter]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/companies${params}`);
      setCompanies(response.data);
    } catch (error) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error('Name, Email and Phone are required');
        return;
      }
      
      // Flatten address for API
      const payload = {
        ...formData,
        address: formData.address?.address_line1 || '',
        city: formData.address?.city || '',
        state: formData.address?.state || '',
        country: formData.address?.country || 'India',
        pin_code: formData.address?.pin_code || ''
      };
      
      await api.post('/companies', payload);
      toast.success('Company created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create company'));
    }
  };

  const handleUpdate = async () => {
    try {
      // Flatten address for API
      const payload = {
        ...formData,
        address: formData.address?.address_line1 || '',
        city: formData.address?.city || '',
        state: formData.address?.state || '',
        country: formData.address?.country || 'India',
        pin_code: formData.address?.pin_code || ''
      };
      
      await api.put(`/companies/${selectedCompany.id}`, payload);
      toast.success('Company updated successfully');
      setShowEditDialog(false);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update company'));
    }
  };

  const handleStatusChange = async (companyId, newStatus) => {
    try {
      await api.patch(`/companies/${companyId}/status?status=${newStatus}`);
      toast.success(`Company status updated to ${newStatus}`);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update status'));
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This will only work if the company has no hotels.')) return;
    
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success('Company deleted successfully');
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete company'));
    }
  };

  // Reset hotel form
  const resetHotelForm = () => {
    setHotelFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      admin_name: '',
      admin_email: '',
      admin_password: ''
    });
  };

  // Open add hotel dialog
  const openAddHotelDialog = (company) => {
    setSelectedCompany(company);
    resetHotelForm();
    setShowAddHotelDialog(true);
  };

  // Handle add hotel
  const handleAddHotel = async () => {
    if (!hotelFormData.name || !hotelFormData.email || !hotelFormData.phone || !hotelFormData.address) {
      toast.error('Name, Email, Phone, and Address are required');
      return;
    }
    if (!hotelFormData.city || !hotelFormData.state) {
      toast.error('City and State are required');
      return;
    }
    
    try {
      setAddingHotel(true);
      await api.post(`/companies/${selectedCompany.id}/hotels`, hotelFormData);
      toast.success('Hotel created successfully');
      setShowAddHotelDialog(false);
      resetHotelForm();
      fetchCompanies();  // Refresh to show new hotel
      
      // Refresh selected company details if dialog is open
      if (showDetailsDialog) {
        const response = await api.get(`/companies/${selectedCompany.id}`);
        setSelectedCompany(response.data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create hotel'));
    } finally {
      setAddingHotel(false);
    }
  };

  const openDeleteAllDialog = async (company) => {
    setSelectedCompany(company);
    setDeleteConfirmation('');
    setDataSummary(null);
    setShowDeleteAllDialog(true);
    
    // Fetch data summary
    try {
      setLoadingSummary(true);
      const response = await api.get(`/companies/${company.id}/data-summary`);
      setDataSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch data summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleDeleteAllData = async () => {
    if (!selectedCompany || deleteConfirmation !== selectedCompany.name) {
      toast.error('Please type the company name to confirm deletion');
      return;
    }
    
    try {
      setDeleting(true);
      const response = await api.delete(
        `/companies/${selectedCompany.id}/all-data?confirmation=${encodeURIComponent(deleteConfirmation)}`
      );
      toast.success(response.data.message);
      setShowDeleteAllDialog(false);
      setSelectedCompany(null);
      setDeleteConfirmation('');
      setDataSummary(null);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete company data'));
    } finally {
      setDeleting(false);
    }
  };

  const openModulesDialog = (company) => {
    setSelectedCompany(company);
    setModuleSettings(company.enabled_modules || []);
    setShowModulesDialog(true);
  };

  const toggleModule = (moduleId) => {
    setModuleSettings(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(m => m !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handleSaveModules = async () => {
    if (!selectedCompany) return;
    
    try {
      setSavingModules(true);
      await api.put(`/companies/${selectedCompany.id}/modules`, {
        enabled_modules: moduleSettings
      });
      toast.success('Module settings updated successfully');
      setShowModulesDialog(false);
      fetchCompanies();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update module settings'));
    } finally {
      setSavingModules(false);
    }
  };

  const viewDetails = async (company) => {
    try {
      const response = await api.get(`/companies/${company.id}`);
      setSelectedCompany(response.data);
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch company details');
    }
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      legal_name: company.legal_name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: {
        address_line1: company.address || '',
        address_line2: '',
        city: company.city || '',
        state: company.state || '',
        pin_code: company.pin_code || '',
        country: company.country || 'India'
      },
      pan_number: company.pan_number || '',
      gstin: company.gstin || '',
      finance_coa_type: company.finance_coa_type || 'company'
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      legal_name: '',
      email: '',
      phone: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pin_code: '',
        country: 'India'
      },
      pan_number: '',
      gstin: '',
      finance_coa_type: 'company'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: 'default', icon: CheckCircle, className: 'bg-green-100 text-green-700' },
      suspended: { variant: 'destructive', icon: XCircle, className: 'bg-red-100 text-red-700' },
      trial: { variant: 'secondary', icon: Clock, className: 'bg-yellow-100 text-yellow-700' }
    };
    const config = variants[status] || variants.trial;
    const Icon = config.icon;
    
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    company.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (company.legal_name && company.legal_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="company-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-8 w-8 text-orange-500" />
            Company Management
          </h1>
          <p className="text-gray-600 mt-1">Manage companies and their hotels</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          data-testid="create-company-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Companies</p>
                <p className="text-2xl font-bold">{companies.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {companies.filter(c => c.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Hotels</p>
                <p className="text-2xl font-bold">
                  {companies.reduce((acc, c) => acc + (c.hotel_count || 0), 0)}
                </p>
              </div>
              <Hotel className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold">
                  {companies.reduce((acc, c) => acc + (c.user_count || 0), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-companies"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>Hotels</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                      <span className="ml-2">Loading...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredCompanies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    No companies found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCompanies.map((company) => (
                  <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{company.name}</p>
                        {company.legal_name && (
                          <p className="text-sm text-gray-500">{company.legal_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{company.email}</p>
                        <p className="text-gray-500">{company.phone}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{company.gstin || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <Hotel className="h-3 w-3 mr-1" />
                        {company.hotel_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <Users className="h-3 w-3 mr-1" />
                        {company.user_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(company.enabled_modules || []).length === 0 ? (
                          <span className="text-xs text-gray-400">No modules</span>
                        ) : (
                          (company.enabled_modules || []).map(mod => {
                            const moduleConfig = AVAILABLE_MODULES.find(m => m.id === mod);
                            if (!moduleConfig) return null;
                            const Icon = moduleConfig.icon;
                            return (
                              <Badge key={mod} variant="outline" className={`text-xs ${moduleConfig.color}`}>
                                <Icon className="h-3 w-3 mr-1" />
                                {moduleConfig.name}
                              </Badge>
                            );
                          })
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(company.status)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewDetails(company)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(company)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModulesDialog(company)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Manage Modules
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleStatusChange(company.id, 'active')}>
                            <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                            Activate
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleStatusChange(company.id, 'suspended')}>
                            <XCircle className="h-4 w-4 mr-2 text-orange-500" />
                            Suspend
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDelete(company.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete (Empty Only)
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedCompany(company);
                              setShowSecureDeleteDialog(true);
                            }}
                            className="text-red-600 font-semibold"
                          >
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            Delete All Data (Secure)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
            <DialogDescription>
              Add a new company to the platform. Finance settings and chart of accounts will be auto-created.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter company name"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label htmlFor="legal_name">Legal Name</Label>
                <Input
                  id="legal_name"
                  value={formData.legal_name}
                  onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                  placeholder="Legal registered name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="company@example.com"
                  data-testid="company-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(value) => setFormData({ ...formData, phone: value })}
                  data-testid="company-phone-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pan_number">PAN Number</Label>
                <Input
                  id="pan_number"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                  placeholder="AAAAA1234A"
                  maxLength={10}
                />
              </div>
              <div>
                <Label htmlFor="gstin">GSTIN</Label>
                <Input
                  id="gstin"
                  value={formData.gstin}
                  onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <AddressInput
                value={formData.address}
                onChange={(value) => setFormData({ ...formData, address: value })}
                data-testid="company-address-input"
              />
            </div>
            
            <div>
              <Label htmlFor="finance_coa_type">Chart of Accounts Type</Label>
              <Select 
                value={formData.finance_coa_type} 
                onValueChange={(value) => setFormData({ ...formData, finance_coa_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company Level (Shared across hotels)</SelectItem>
                  <SelectItem value="hotel">Hotel Level (Separate for each hotel)</SelectItem>
                  <SelectItem value="both">Both (Company + Hotel Level)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Choose whether finance/accounting is managed at company level, per hotel, or both
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-company-btn">Create Company</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>Update company information</DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Company Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-legal_name">Legal Name</Label>
                <Input
                  id="edit-legal_name"
                  value={formData.legal_name}
                  onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone *</Label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(value) => setFormData({ ...formData, phone: value })}
                  data-testid="edit-company-phone-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-pan">PAN Number</Label>
                <Input
                  id="edit-pan"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                  maxLength={10}
                />
              </div>
              <div>
                <Label htmlFor="edit-gstin">GSTIN</Label>
                <Input
                  id="edit-gstin"
                  value={formData.gstin}
                  onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                  maxLength={15}
                />
              </div>
            </div>
            
            <div>
              <Label>Address</Label>
              <AddressInput
                value={formData.address}
                onChange={(value) => setFormData({ ...formData, address: value })}
                data-testid="edit-company-address-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-500" />
              {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedCompany?.legal_name || 'Company Details'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCompany && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="info">Company Info</TabsTrigger>
                <TabsTrigger value="hotels">Hotels ({selectedCompany.hotels?.length || 0})</TabsTrigger>
                <TabsTrigger value="roomtypes">Room Types</TabsTrigger>
                <TabsTrigger value="finance">Finance Settings</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium">{selectedCompany.email}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone</Label>
                    <p className="font-medium">{selectedCompany.phone}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">PAN</Label>
                    <p className="font-medium font-mono">{selectedCompany.pan_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">GSTIN</Label>
                    <p className="font-medium font-mono">{selectedCompany.gstin || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-500">Address</Label>
                    <p className="font-medium">
                      {[selectedCompany.address, selectedCompany.city, selectedCompany.state, selectedCompany.country]
                        .filter(Boolean).join(', ') || '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedCompany.status)}</div>
                  </div>
                  <div>
                    <Label className="text-gray-500">Created</Label>
                    <p className="font-medium">
                      {new Date(selectedCompany.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="hotels" className="mt-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-700">Hotels ({selectedCompany.hotels?.length || 0})</h4>
                  <Button 
                    size="sm" 
                    onClick={() => openAddHotelDialog(selectedCompany)}
                    data-testid="add-hotel-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Hotel
                  </Button>
                </div>
                {selectedCompany.hotels?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedCompany.hotels.map((hotel) => (
                      <Card key={hotel.id}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Hotel className="h-8 w-8 text-blue-500" />
                            <div>
                              <p className="font-medium">{hotel.name}</p>
                              <p className="text-sm text-gray-500">{hotel.city}, {hotel.state}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(hotel.status)}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedHotelForAmenities(hotel);
                                setAmenitiesDialogOpen(true);
                              }}
                              data-testid={`hotel-amenities-btn-${hotel.id}`}
                            >
                              <Layers className="h-3 w-3 mr-1" />
                              Amenities
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedHotelForConfig(hotel);
                                setRoomTypeConfigDialogOpen(true);
                              }}
                            >
                              <Settings className="h-3 w-3 mr-1" />
                              Configure
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Hotel className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hotels added yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-3"
                      onClick={() => openAddHotelDialog(selectedCompany)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add First Hotel
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* Room Types Tab */}
              <TabsContent value="roomtypes" className="mt-4">
                <RoomTypeAmenitiesTab 
                  companyId={selectedCompany.id} 
                  hotels={selectedCompany.hotels || []} 
                />
              </TabsContent>
              
              <TabsContent value="finance" className="mt-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Chart of Accounts Type</Label>
                      <p className="font-medium">
                        {selectedCompany.finance_coa_type === 'both' 
                          ? 'Company + Hotel Level' 
                          : `${selectedCompany.finance_coa_type?.charAt(0).toUpperCase()}${selectedCompany.finance_coa_type?.slice(1)} Level`}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Users</Label>
                      <p className="font-medium">{selectedCompany.user_count || 0}</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <Button variant="outline" className="w-full">
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Finance Settings
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Company Data Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Company and All Data
            </DialogTitle>
            <DialogDescription>
              This action is <strong>irreversible</strong>. All company data including hotels, users, bookings, invoices, and finance records will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          
          {loadingSummary ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3">Loading data summary...</span>
            </div>
          ) : dataSummary && (
            <div className="space-y-4">
              {/* Company Info */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {dataSummary.company.name}
                </h4>
                <p className="text-sm text-red-600 mt-1">
                  Status: {dataSummary.company.status}
                </p>
              </div>
              
              {/* Data to be deleted */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4 text-gray-500" />
                  Data that will be deleted:
                </h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Hotel className="h-4 w-4" />
                      Hotels
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{dataSummary.counts.hotels}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="h-4 w-4" />
                      Users
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{dataSummary.counts.users}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-gray-600">
                      <FileText className="h-4 w-4" />
                      Bookings
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{dataSummary.counts.bookings}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-gray-600">
                      <FileText className="h-4 w-4" />
                      Invoices
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{dataSummary.counts.invoices}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="h-4 w-4" />
                      Guests
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{dataSummary.counts.guests}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-gray-600">
                      <ShoppingCart className="h-4 w-4" />
                      Orders
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{dataSummary.counts.orders}</p>
                  </div>
                </div>
                
                {dataSummary.hotels.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-2">Hotels to be deleted:</p>
                    <div className="flex flex-wrap gap-2">
                      {dataSummary.hotels.map((hotel) => (
                        <Badge key={hotel.id} variant="outline" className="text-red-600 border-red-200">
                          {hotel.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Confirmation Input */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <Label className="text-yellow-800 font-semibold">
                  Type &quot;{selectedCompany?.name}&quot; to confirm deletion:
                </Label>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder={selectedCompany?.name}
                  className="mt-2 border-yellow-300 focus:border-yellow-500"
                  data-testid="delete-confirmation-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteAllDialog(false);
                setDeleteConfirmation('');
                setDataSummary(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteAllData}
              disabled={deleting || deleteConfirmation !== selectedCompany?.name}
              data-testid="confirm-delete-all-btn"
            >
              {deleting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Everything
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secure Delete Dialog with Dual OTP */}
      <DeleteEntityDialog
        open={showSecureDeleteDialog}
        onOpenChange={setShowSecureDeleteDialog}
        entityType="company"
        entityId={selectedCompany?.id}
        entityName={selectedCompany?.name}
        onDeleted={() => {
          setSelectedCompany(null);
          fetchCompanies();
        }}
      />

      {/* Module Settings Dialog */}
      <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-500" />
              Module Settings
            </DialogTitle>
            <DialogDescription>
              Enable or disable modules for <strong>{selectedCompany?.name}</strong>. 
              Only enabled modules will be accessible to company and hotel users.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4 pr-2">
            {AVAILABLE_MODULES.map((module) => {
              const Icon = module.icon;
              const isEnabled = moduleSettings.includes(module.id);
              
              return (
                <div 
                  key={module.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    isEnabled 
                      ? 'border-green-200 bg-green-50/50' 
                      : 'border-gray-200 bg-gray-50/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${module.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{module.name}</p>
                      <p className="text-sm text-gray-500">{module.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => toggleModule(module.id)}
                    data-testid={`module-toggle-${module.id}`}
                  />
                </div>
              );
            })}
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Disabled modules will be completely hidden from Company Admin, Hotel Admin, and all staff users. Only Super Admin can change these settings.
            </p>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowModulesDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveModules}
              disabled={savingModules}
              data-testid="save-modules-btn"
            >
              {savingModules ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Hotel Dialog */}
      <Dialog open={showAddHotelDialog} onOpenChange={setShowAddHotelDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hotel className="h-5 w-5 text-blue-500" />
              Add Hotel to {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription>
              Create a new hotel under this company. You can optionally create a hotel admin account.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Hotel Name *</Label>
              <Input
                value={hotelFormData.name}
                onChange={(e) => setHotelFormData({...hotelFormData, name: e.target.value})}
                placeholder="e.g., Grand Hotel Mumbai"
                data-testid="hotel-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={hotelFormData.email}
                  onChange={(e) => setHotelFormData({...hotelFormData, email: e.target.value})}
                  placeholder="hotel@example.com"
                  data-testid="hotel-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={hotelFormData.phone}
                  onChange={(e) => setHotelFormData({...hotelFormData, phone: e.target.value})}
                  placeholder="+91 9876543210"
                  data-testid="hotel-phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Address *</Label>
              <Input
                value={hotelFormData.address}
                onChange={(e) => setHotelFormData({...hotelFormData, address: e.target.value})}
                placeholder="123 Main Street"
                data-testid="hotel-address-input"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Input
                  value={hotelFormData.city}
                  onChange={(e) => setHotelFormData({...hotelFormData, city: e.target.value})}
                  placeholder="Mumbai"
                  data-testid="hotel-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label>State *</Label>
                <Input
                  value={hotelFormData.state}
                  onChange={(e) => setHotelFormData({...hotelFormData, state: e.target.value})}
                  placeholder="Maharashtra"
                  data-testid="hotel-state-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={hotelFormData.country}
                  onChange={(e) => setHotelFormData({...hotelFormData, country: e.target.value})}
                  placeholder="India"
                />
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium text-gray-700">Hotel Admin (Optional)</Label>
              <p className="text-xs text-gray-500 mb-3">Create a hotel admin account for this hotel</p>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Admin Name</Label>
                  <Input
                    value={hotelFormData.admin_name}
                    onChange={(e) => setHotelFormData({...hotelFormData, admin_name: e.target.value})}
                    placeholder="Hotel Admin Name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Admin Email</Label>
                    <Input
                      type="email"
                      value={hotelFormData.admin_email}
                      onChange={(e) => setHotelFormData({...hotelFormData, admin_email: e.target.value})}
                      placeholder="admin@hotel.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Admin Password</Label>
                    <Input
                      type="password"
                      value={hotelFormData.admin_password}
                      onChange={(e) => setHotelFormData({...hotelFormData, admin_password: e.target.value})}
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddHotelDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddHotel} disabled={addingHotel} data-testid="create-hotel-btn">
              {addingHotel ? 'Creating...' : 'Create Hotel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Type Configuration Dialog */}
      <RoomTypeConfigDialog
        open={roomTypeConfigDialogOpen}
        onOpenChange={setRoomTypeConfigDialogOpen}
        hotelId={selectedHotelForConfig?.id}
        hotelName={selectedHotelForConfig?.name}
      />

      {/* Hotel Amenities Allocation Dialog */}
      <HotelAmenitiesAllocationDialog
        open={amenitiesDialogOpen}
        onOpenChange={setAmenitiesDialogOpen}
        hotelId={selectedHotelForAmenities?.id}
        hotelName={selectedHotelForAmenities?.name}
      />
    </div>
  );
}
